import { ErrorNotice } from '@/components/ui/Feedback'
import { ListMusic, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'

import { ApiError } from '@/api/client'
import { useBulkAddToPlaylist, useCreatePlaylist, usePlaylists } from '@/api/hooks'
import type { BulkPlaylistResult, Playlist, Provider } from '@/api/types'
import { Button, EmptyState, Input, Sheet, Skeleton, Spinner, toast } from '@/components/ui'
import { PlaylistMosaic } from '@/features/playlists/PlaylistMosaic'

export interface AddToPlaylistSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Tracks already in the library, by `track.id` (not the library item id). */
  trackIds?: number[]
  /** Provider tracks to add to the playlist and permanent library. */
  providerRefs?: Array<{ provider: Provider; external_id: string }>
  /** Called with however many actually landed, which can be fewer than requested. */
  onAdded?: (count: number) => void
}

function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof ApiError) return error.message || fallback
  // fetch() rejects with a TypeError when the network itself failed.
  if (error instanceof TypeError) return 'Could not reach the server. Check your connection.'
  return fallback
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The first distinct reason, so a hundred identical failures read as one sentence. */
function firstReason(result: BulkPlaylistResult): string | null {
  return result.failed.find((entry) => entry.detail)?.detail ?? null
}

/**
 * Put a multi-selection into a playlist.
 *
 * Accepts library tracks, provider results, or both, because the same sheet is
 * opened from the library, from search, and from an album. Playlist additions
 * also join the permanent library and download automatically.
 */
export function AddToPlaylistSheet({
  open,
  onOpenChange,
  trackIds,
  providerRefs,
  onAdded,
}: AddToPlaylistSheetProps) {
  const playlists = usePlaylists()
  const bulkAdd = useBulkAddToPlaylist()
  const createPlaylist = useCreatePlaylist()

  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<number | null>(null)

  // A reopened sheet starts clean; a stale error from last time is confusing.
  useEffect(() => {
    if (!open) return
    setCreating(false)
    setName('')
    setNameError(undefined)
    setError(null)
  }, [open])

  const ids = trackIds ?? []
  const refs = providerRefs ?? []
  const total = ids.length + refs.length
  const busy = bulkAdd.isPending || createPlaylist.isPending

  // Undefined rather than an empty array, so the request carries only the half
  // that is actually in play.
  function payload(playlistId: number): {
    playlistId: number
    track_ids?: number[]
    tracks?: Array<{ provider: Provider; external_id: string }>
  } {
    return {
      playlistId,
      track_ids: ids.length > 0 ? ids : undefined,
      tracks: refs.length > 0 ? refs : undefined,
    }
  }

  function report(playlist: Playlist, result: BulkPlaylistResult): void {
    const failed = result.failed.length

    if (result.added === 0) {
      setError(
        failed > 0
          ? `Nothing was added to ${playlist.name}. ${firstReason(result) ?? 'The songs were rejected.'}`
          : `Nothing was added to ${playlist.name}. They may already be in it.`,
      )
      return
    }

    const notes: string[] = []
    if (failed > 0) {
      notes.push(`${plural(failed, 'song', 'songs')} could not be added.`)
      const reason = firstReason(result)
      if (reason) notes.push(reason)
    }
    notes.push('Songs are also kept in your library.')

    toast.success(
      `Added ${plural(result.added, 'song', 'songs')} to ${playlist.name}`,
      notes.join(' '),
    )
    onAdded?.(result.added)
    onOpenChange(false)
  }

  function addTo(playlist: Playlist): void {
    if (total === 0) return
    setError(null)
    setPendingId(playlist.id)
    bulkAdd.mutate(payload(playlist.id), {
      onSuccess: (result) => report(playlist, result),
      onError: (cause) => setError(errorMessage(cause)),
      onSettled: () => setPendingId(null),
    })
  }

  async function createAndAdd(): Promise<void> {
    if (busy || total === 0) return
    const trimmed = name.trim()
    setError(null)
    setNameError(undefined)
    if (!trimmed) {
      setNameError('Give the playlist a name.')
      return
    }
    try {
      const playlist = await createPlaylist.mutateAsync({ name: trimmed })
      // Keep the new playlist visible if adding fails, so retry cannot create a duplicate.
      setCreating(false)
      setName('')
      const result = await bulkAdd.mutateAsync(payload(playlist.id))
      report(playlist, result)
    } catch (cause) {
      if (cause instanceof ApiError) setNameError(cause.fieldError('name'))
      setError(errorMessage(cause))
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next && busy) return
        onOpenChange(next)
      }}
      title="Add to playlist"
      description={`${plural(total, 'song', 'songs')} selected. Songs are also kept in your library.`}
    >
      {playlists.isPending ? (
        <div role="status" aria-label="Loading playlists">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex h-16 items-center gap-3 px-2">
              <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <Skeleton className="h-3.5 w-1/2 max-w-48" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </div>
      ) : playlists.isError ? (
        <ErrorNotice onRetry={() => void playlists.refetch()} busy={playlists.isFetching}>{errorMessage(playlists.error)}</ErrorNotice>
      ) : playlists.data && playlists.data.length > 0 ? (
        <ul>
          {playlists.data.map((playlist) => (
            <li key={playlist.id}>
              <button
                type="button"
                onClick={() => addTo(playlist)}
                disabled={busy || total === 0}
                className="flex h-16 w-full items-center gap-3 rounded-lg px-2 text-left transition-colors hover:bg-surface-hover disabled:opacity-50"
              >
                <PlaylistMosaic coverUrls={playlist.cover_urls} className="w-10 shrink-0 rounded-md" />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-text">{playlist.name}</span>
                  <span className="text-sm text-text-muted">
                    {plural(playlist.track_count, 'song', 'songs')}
                  </span>
                </span>
                {pendingId === playlist.id ? <Spinner size="sm" label={null} /> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={ListMusic}
          title="No playlists yet"
          description="Create one below and your selection goes straight into it."
          className="py-8"
        />
      )}

      <div className="mt-2 border-t border-border px-2 pt-3">
        {creating ? (
          <div className="flex flex-col gap-3">
            <Input
              label="Playlist name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              error={nameError}
              autoFocus
              maxLength={100}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void createAndAdd()
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCreating(false)} disabled={busy}>
                Cancel
              </Button>
              <Button onClick={() => void createAndAdd()} loading={busy} disabled={total === 0}>
                Create and add
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="secondary"
            fullWidth
            onClick={() => setCreating(true)}
            disabled={busy || total === 0}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            New playlist
          </Button>
        )}
      </div>

      {error ? (
        <ErrorNotice onDismiss={() => setError(null)}>{error}</ErrorNotice>
      ) : null}
    </Sheet>
  )
}
